import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Col, Row, Tooltip } from 'antd';
import {
  mdiAccountGroupOutline, mdiShieldCheckOutline, mdiShieldOutline,
} from '@mdi/js';
import { useTranslation } from 'react-i18next';

import {
  getMyPermissions, getMeIsRoot, getMeIsRootOrAdmin,
} from '../selectors';
import { getAllServicesMap } from '../../actors/selectors';
import InfoBlock from '../../components/InfoBlock/InfoBlock';
import BaseCard from '../../_ui/BaseCard/BaseCard';
import BaseTag from '../../_ui/BaseTag/BaseTag';
import BaseIcon from '../../_ui/BaseIcon/BaseIcon';
import { capitalize } from '../../mainUtils';

function MyPermissionsSection() {
  const { t } = useTranslation();
  const myPermissions = useSelector(getMyPermissions);
  const allServicesMap = useSelector(getAllServicesMap);
  const meIsRoot = useSelector(getMeIsRoot);
  const meIsRootOrAdmin = useSelector(getMeIsRootOrAdmin);
  const meIsAdmin = !meIsRoot && meIsRootOrAdmin;

  const actorPerms = myPermissions?.actor || [];
  const groupPerms = myPermissions?.group || [];

  const serviceUUIDs = useMemo(() => {
    const uuids = new Set([
      ...actorPerms.map((p) => p.service_uuid),
      ...groupPerms.map((p) => p.service_uuid),
    ]);
    return [...uuids];
  }, [actorPerms, groupPerms]);

  const getServiceName = (uuid) => {
    const service = allServicesMap instanceof Map ? allServicesMap.get(uuid) : null;
    return service?.uinfo?.service_name || uuid;
  };

  const renderPermRow = (perm, idx) => (
    <Row
      key={`${perm.permaction_uuid}_${perm.actor_uuid || idx}`}
      className="mb-1"
      gutter={[8, 0]}
      align="middle"
      wrap={false}
    >
      <Col flex="auto">
        <Tooltip title={perm.permaction_uuid}>
          <span className="font-mono" style={{ fontSize: 12 }}>
            {perm.permaction_uuid ? `${perm.permaction_uuid.slice(0, 8)}…` : '—'}
          </span>
        </Tooltip>
      </Col>
      <Col>
        <BaseTag color={perm.value ? 'green' : 'red'}>
          {perm.value
            ? capitalize(t('auth.labels.allowed', 'allowed'))
            : capitalize(t('auth.labels.denied', 'denied'))}
        </BaseTag>
      </Col>
    </Row>
  );

  const rootCapabilities = [
    t('auth.capabilities.root_1', 'Full access to all services in the 54origins ecosystem'),
    t('auth.capabilities.root_2', 'Manage all actors: create, edit, delete any user, group or service'),
    t('auth.capabilities.root_3', 'Manage permissions and groups across the entire biome'),
    t('auth.capabilities.root_4', 'Run full force synchronization between services'),
    t('auth.capabilities.root_5', 'Manage public interfaces, security policy, SSL certificates'),
    t('auth.capabilities.root_6', 'Masquerade as any user including admins'),
    t('auth.capabilities.root_7', 'Reset sessions for any service'),
    t('auth.capabilities.root_8', 'Create and delete services in the biome'),
  ];

  const adminCapabilities = [
    t('auth.capabilities.admin_1', 'Manage actors: create, edit, delete non-root users'),
    t('auth.capabilities.admin_2', 'Manage groups and assign permissions'),
    t('auth.capabilities.admin_3', 'View biome dashboard and synchronization info'),
    t('auth.capabilities.admin_4', 'Run partial force synchronization (groups, services, permissions)'),
    t('auth.capabilities.admin_5', 'Manage public interfaces for services'),
    t('auth.capabilities.admin_6', 'Masquerade as regular users (not root)'),
    t('auth.capabilities.admin_7', 'Reset sessions for services'),
  ];

  if (meIsRoot || meIsAdmin) {
    const capabilities = meIsRoot ? rootCapabilities : adminCapabilities;
    return (
      <div style={{ width: '100%' }}>
        <Row gutter={[24, 16]} className="mt-4">
          <Col span={14}>
            <BaseCard>
              <InfoBlock
                iconPath={mdiShieldCheckOutline}
                title={capitalize(t('auth.headers.permissions', 'permissions'))}
              >
                <Row gutter={[8, 8]} className="mb-3">
                  {meIsRoot && <Col><BaseTag color="gold">Root</BaseTag></Col>}
                  {meIsAdmin && (
                    <Col>
                      <BaseTag color="blue">{capitalize(t('auth.labels.admin', 'admin'))}</BaseTag>
                    </Col>
                  )}
                </Row>
                <ul style={{ paddingLeft: 18, margin: 0 }}>
                  {capabilities.map((cap, i) => (
                    <li key={i} style={{ marginBottom: 6, fontSize: 13 }}>{cap}</li>
                  ))}
                </ul>
              </InfoBlock>
            </BaseCard>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <Row gutter={[24, 16]} className="mt-4">
        <Col span={14}>
          <BaseCard>
            <InfoBlock
              iconPath={mdiShieldCheckOutline}
              title={capitalize(t('auth.headers.permissions', 'permissions'))}
            >
              <div>
                {serviceUUIDs.length === 0 && (
                  <span style={{ color: '#8c8c8c', fontSize: 13 }}>
                    {capitalize(t('auth.messages.no_permissions', 'no permissions assigned'))}
                  </span>
                )}
                {serviceUUIDs.map((serviceUUID, sIdx) => {
                  const permsForActor = actorPerms.filter(
                    (p) => p.service_uuid === serviceUUID,
                  );
                  const permsForGroups = groupPerms.filter(
                    (p) => p.service_uuid === serviceUUID,
                  );

                  return (
                    <div key={serviceUUID} className={sIdx > 0 ? 'mt-4' : ''}>
                      <p className="mb-2" style={{ fontWeight: 600 }}>
                        {getServiceName(serviceUUID)}
                      </p>

                      {permsForActor.length > 0 && (
                        <div className="mb-3">
                          <Row gutter={[4, 0]} align="middle" className="mb-1">
                            <Col>
                              <BaseIcon path={mdiShieldOutline} size={0.75} />
                            </Col>
                            <Col>
                              <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                                {capitalize(t('auth.headers.personal_permissions', 'personal'))}
                              </span>
                            </Col>
                          </Row>
                          {permsForActor.map((p, i) => renderPermRow(p, i))}
                        </div>
                      )}

                      {permsForGroups.length > 0 && (
                        <div>
                          <Row gutter={[4, 0]} align="middle" className="mb-1">
                            <Col>
                              <BaseIcon path={mdiAccountGroupOutline} size={0.75} />
                            </Col>
                            <Col>
                              <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                                {capitalize(t('auth.headers.group_permissions', 'via groups'))}
                              </span>
                            </Col>
                          </Row>
                          {permsForGroups.map((p, i) => renderPermRow(p, i))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </InfoBlock>
          </BaseCard>
        </Col>
      </Row>
    </div>
  );
}

export default MyPermissionsSection;
